import { useState, useEffect } from 'react'
import { db } from '../lib/db'
import { golfapi } from '../lib/golfapi'
import { golfcourseapi } from '../lib/golfcourseapi'
import { searchCourse } from '../lib/searchCourse'
import HoleSchematic from './HoleSchematic'
import { teeGenderSuffix } from '../lib/tee'

// External course IDs are namespaced 'gca_<id>' for GolfCourseAPI rows so
// they don't collide with GolfAPI.io's numeric IDs in the unique index.
// Pick the right provider's detail endpoint based on the prefix.
const isGcaId = (extId) => typeof extId === 'string' && extId.startsWith('gca_')

const isUuid = (v) => typeof v === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)

// Whatever courses we already hold for this club, locally (seed or cached).
async function localCourses(club) {
  if (club.courses) return club.courses                                          // search-path club
  if (club.memberIds?.length) {                                                  // collapsed multi-club resort
    const lists = await Promise.all(club.memberIds.map((id) =>
      db('get_club_courses', { club_id: id }).then((r) => r.data || []).catch(() => [])))
    return lists.flat()
  }
  if (club.is_club) {
    const { data } = await db('get_club_courses', { club_id: club.id }).catch(() => ({ data: [] }))
    return data || []
  }
  return [{ id: club.id, name: club.name }]                                       // a course standing in as its own club
}

// Tapping a "course" (really a club) opens this floating popup: club details →
// a dropdown of the club's courses → tee yardages/rating/slope + our by-hole
// drawings for the chosen course. A search club arrives with its GolfAPI
// courses; a featured club loads its courses from our DB; a club with one
// course auto-selects it.
export default function ClubDetailModal({ club, onClose }) {
  const [info, setInfo] = useState(null)      // get_club_detail (address, # holes, …)
  const [courses, setCourses] = useState(null)
  const [picked, setPicked] = useState(-1)    // index into courses
  const [detail, setDetail] = useState(null)  // enriched course (tees, holes_data, par)
  const [coords, setCoords] = useState(null)  // course_coordinates for the schematics
  const [loading, setLoading] = useState(false)

  // Club-level details (address, total holes) — only resolvable when we have a
  // real club UUID (featured clubs). Search clubs fall back to grouping fields.
  useEffect(() => {
    let cancelled = false
    const detailId = club.memberIds?.[0] || club.id
    if (!isUuid(detailId)) { setInfo(null); return }
    db('get_club_detail', { club_id: detailId })
      .then(({ data }) => { if (!cancelled) setInfo(data || null) })
      .catch(() => { if (!cancelled) setInfo(null) })
    return () => { cancelled = true }
  }, [club])

  // Resolve the club's courses. Gather whatever we hold locally first; if none of
  // it is GolfAPI-backed yet, ENRICH THIS CLUB ROW IN PLACE — the server writes
  // the matched GolfAPI club's data onto the selected row (no duplicate rows) and
  // returns its real course list. Someone browsing a club is likely about to play
  // it, so the lookup is worth the quota.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const seed = await localCourses(club)
      if (cancelled) return
      const preloaded = seed.length > 0 && seed.every((c) => c.external_course_id)
      if (preloaded) { setCourses(seed); return }
      try {
        // GolfAPI.io first (POI-rich); if no match or degraded, fall through
        // to GolfCourseAPI (broader coverage, no per-hole POIs). Either path
        // writes onto the LOCAL club row in place — no duplicate rows.
        let res = await golfapi('enrich_club', {
          club_id: club.id, name: club.name, city: club.city, state: club.state,
        })
        if (cancelled) return
        if (!res || res.degraded || !res.courses?.length) {
          res = await golfcourseapi('enrich_club', {
            club_id: club.id, name: club.name, city: club.city, state: club.state,
          })
          if (cancelled) return
        }
        const courses = (!res?.degraded && res.courses?.length) ? res.courses : []
        setCourses(courses.length ? courses : seed)
        // Enrich filled in the club row (address/phone/website) — refresh header.
        if (!res?.degraded && res.clubId) {
          db('get_club_detail', { club_id: res.clubId })
            .then(({ data }) => { if (data && !cancelled) setInfo((prev) => ({ ...(prev || {}), ...data })) })
            .catch(() => {})
        }
      } catch { if (!cancelled) setCourses(seed) }
    })()
    return () => { cancelled = true }
  }, [club])

  // Auto-select when there's only one course.
  useEffect(() => {
    if (courses && courses.length === 1 && picked === -1) pickCourse(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses])

  async function pickCourse(idx) {
    setPicked(idx)
    setDetail(null); setCoords(null)
    if (idx < 0) return
    const c = courses[idx]
    setLoading(true)
    try {
      let courseId = isUuid(c.id) ? c.id : null
      if (!courseId && c.external_course_id) {
        // Provider routing by ID prefix: 'gca_' → GolfCourseAPI, else → GolfAPI.io.
        const det = isGcaId(c.external_course_id)
          ? await golfcourseapi('detail', { courseID: c.external_course_id })
          : await golfapi('detail', { courseID: c.external_course_id })
        courseId = det?.courseId || det?.course_id || null
      }
      if (courseId) {
        const json = await searchCourse({ id: courseId })
        const c0 = json?.courses?.[0] || null
        setDetail(c0)
        // The detail fetch upserts the real GolfAPI club (address/phone/website),
        // so refresh the header info from that club rather than the seed row.
        if (c0?.club_id) db('get_club_detail', { club_id: c0.club_id })
          .then(({ data }) => { if (data) setInfo(data) }).catch(() => {})
        try { const { data } = await db('get_course_coordinates', { course_id: courseId }); setCoords(data || []) }
        catch { setCoords([]) }
      } else { setDetail(null); setCoords([]) }
    } catch { setDetail(null); setCoords([]) }
    setLoading(false)
  }

  // Club contact lines from whatever fields we have (GolfAPI detail fills these
  // in after a course is selected).
  const a = info || {}
  const cityLine = [a.city || club.city, [a.state || club.state, a.postal_code].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ')
  const addr = [a.address, cityLine].filter(Boolean)

  // Only claim a hole count we can back: the selected course's GolfAPI-declared
  // value. The 15k local seed rows have an unverified holes column (that's why a
  // test course read "7"), so we don't surface a number for them.
  const holeCount = detail?.external_course_id ? (detail.holes ?? null) : null
  const tees = detail?.tees || []
  const par = detail?.par ?? (detail?.holes_data?.length
    ? detail.holes_data.reduce((s, h) => s + (h.par || 0), 0) : null)
  const holesData = detail?.holes_data || []

  // Only draw holes we actually have coordinate data for — a course with no
  // generated layouts shows a single note instead of a wall of empty boxes.
  const coordHoles = new Set((coords || []).map((c) => Number(c.hole)))
  const drawableHoles = holesData.filter((h) => coordHoles.has(Number(h.hole_number)))

  return (
    <div style={overlay} onClick={onClose}>
      <div className="card" style={sheet} onClick={(e) => e.stopPropagation()}>
        {/* ── Club header ───────────────────────────────────────────── */}
        <p style={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.2 }}>{a.name || club.name}</p>
        {addr.map((l, i) => (
          <p key={i} className="text-muted text-sm" style={{ marginTop: 2 }}>{l}</p>
        ))}
        {a.telephone && (
          <p className="text-muted text-sm" style={{ marginTop: 2 }}>
            <a href={`tel:${a.telephone}`} style={{ color: 'inherit' }}>{a.telephone}</a>
          </p>
        )}
        {a.website && (
          <p className="text-sm" style={{ marginTop: 2 }}>
            <a href={a.website.startsWith('http') ? a.website : `https://${a.website}`} target="_blank" rel="noreferrer"
               style={{ color: 'var(--gold)' }}>{a.website.replace(/^https?:\/\//, '')}</a>
          </p>
        )}
        {holeCount != null && (
          <p className="text-muted text-xs" style={{ marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {holeCount} holes
          </p>
        )}

        {!courses && <div className="spinner" style={{ margin: '20px auto' }} />}

        {/* ── Course: a single course is just shown; multiple get a picker ── */}
        {courses && courses.length === 1 && (
          <div style={{ margin: '14px 0 4px' }}>
            <p style={labelStyle}>Course</p>
            <p style={{ fontWeight: 600 }}>{courses[0].name}</p>
          </div>
        )}
        {courses && courses.length > 1 && (
          <div style={{ margin: '14px 0 4px' }}>
            <p style={labelStyle}>Course</p>
            <select
              className="input"
              value={picked}
              onChange={(e) => pickCourse(Number(e.target.value))}
              style={{ width: '100%' }}
            >
              <option value={-1} disabled>Select a course…</option>
              {courses.map((c, i) => (
                <option key={c.id || c.external_course_id || i} value={i}>
                  {c.name}{c.external_course_id && c.holes ? ` · ${c.holes} holes` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {courses && courses.length === 0 && (
          <p className="text-muted text-sm" style={{ marginTop: 12 }}>No course data available yet.</p>
        )}

        {/* ── Selected course detail ────────────────────────────────── */}
        {picked >= 0 && (
          <div style={{ marginTop: 12 }}>
            {loading && <div className="spinner" style={{ margin: '16px auto' }} />}

            {/* Tee yardages / rating / slope */}
            {!loading && tees.length > 0 && (
              <div style={{ overflowX: 'auto', marginBottom: 14 }}>
                <table style={tbl}>
                  <thead>
                    <tr style={{ color: 'var(--gray-400)', textAlign: 'left' }}>
                      <th style={th}>Tee</th><th style={thR}>Yards</th><th style={thR}>Rating</th><th style={thR}>Slope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {par != null && (
                      <tr style={{ borderTop: '1px solid var(--green-mid)' }}>
                        <td style={td}>Par</td>
                        <td style={tdR}>{par}</td><td style={tdR}>–</td><td style={tdR}>–</td>
                      </tr>
                    )}
                    {tees.map((t, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--green-mid)' }}>
                        <td style={td}>
                          <span style={{
                            display: 'inline-block', width: 9, height: 9, borderRadius: 2, marginRight: 7,
                            verticalAlign: 'middle', background: teeHex(t.tee_color), border: '1px solid rgba(0,0,0,0.3)',
                          }} />
                          {(() => {
                            const base = t.tee_name || t.tee_color || '—'
                            const g = teeGenderSuffix(t)
                            return g ? `${base} (${g})` : base
                          })()}
                        </td>
                        <td style={tdR}>{t.total_yardage || '–'}</td>
                        <td style={tdR}>{t.course_rating ?? '–'}</td>
                        <td style={tdR}>{t.slope_rating ?? '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* By-hole drawings — horizontal scroll, only holes we can draw */}
            {!loading && drawableHoles.length > 0 && (
              <div>
                <p style={labelStyle}>Holes</p>
                <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6, WebkitOverflowScrolling: 'touch' }}>
                  {drawableHoles.map((h) => (
                    <HoleSchematic
                      key={h.hole_number}
                      coords={coords || []}
                      hole={h.hole_number}
                      par={h.par}
                      style={{ flex: '0 0 110px', width: 110, height: 150 }}
                    />
                  ))}
                </div>
              </div>
            )}
            {!loading && coords && drawableHoles.length === 0 && (
              <p className="text-muted text-sm" style={{ marginTop: 4 }}>Hole layouts coming soon.</p>
            )}

            {!loading && tees.length === 0 && holesData.length === 0 && (
              <p className="text-muted text-sm">Course details unavailable yet.</p>
            )}
          </div>
        )}

        <button className="btn btn-ghost btn-full" onClick={onClose} style={{ marginTop: 16 }}>Close</button>
      </div>
    </div>
  )
}

// Map a GolfAPI tee color name to a swatch hex.
function teeHex(c) {
  const m = {
    black: '#111', blue: '#2563eb', white: '#f8fafc', red: '#dc2626', gold: '#D4B97A',
    green: '#16a34a', silver: '#cbd5e1', yellow: '#eab308', orange: '#ea580c', purple: '#7c3aed',
  }
  return m[(c || '').toLowerCase()] || 'var(--green-mid)'
}

const overlay = {
  position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(10,15,22,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
}
const sheet = {
  width: '100%', maxWidth: 480, maxHeight: '88vh', overflowY: 'auto', margin: 0,
}
const labelStyle = { fontSize: '0.72rem', fontWeight: 500, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }
const tbl = { width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }
const th = { padding: '4px 8px', fontWeight: 500 }
const thR = { ...th, textAlign: 'right' }
const td = { padding: '6px 8px' }
const tdR = { ...td, textAlign: 'right' }
